import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PhoneNumberSettingsSideBar from './components/phone-number-settings-sidebar/phone-number-settings-sidebar';
import PhoneNumberSettingsContent from './components/phone-number-settings-content/phone-number-settings-content';
import { getPhoneNumberDetailsRequest } from '../../../../extra-actions';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import { RootState } from '../../../../../../store/root-reducer';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';

const PhoneNumberSettings: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const getPhoneNumberDetailsRequestStatus = useSelector(
    (state: RootState) => state.dialer.getPhoneNumberDetailsRequest.status,
  );
  const [activeSidebarLabel, setActiveSidebarLabel] = useState<string>('Phone Number Label');
  const [isManualScroll, setIsManualScroll] = useState<boolean>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isUpdatingHashRef = useRef<boolean>(false);

  const canAssignPhoneNumber = hasPermission(Permissions.PHONE_NUMBER_ASSIGNMENT_UPDATE);

  const sections = useMemo(() => {
    const allSections = [
      { id: 'phone-number-label', label: 'Phone Number Label' },
      { id: 'assign-number', label: 'Assign Number', hidden: !canAssignPhoneNumber },
      { id: 'call-forwarding', label: 'Call Forwarding' },
      { id: 'call-recording', label: 'Call Recording' },
      { id: 'voicemail-greeting', label: 'Voicemail Greeting' },
    ];
    return allSections.filter((s) => !s.hidden);
  }, [canAssignPhoneNumber]);

  // Create mapping from section ID to label
  const sectionIdToLabel = sections.reduce(
    (acc, section) => {
      acc[section.id] = section.label;
      return acc;
    },
    {} as Record<string, string>,
  );

  //* Helper Functions
  const scrollToSection = useCallback(
    (sectionId: string, offset = 20): boolean => {
      const element = document.getElementById(sectionId);
      const container = scrollContainerRef.current;

      if (!element || !container) {
        return false;
      }

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const { scrollTop } = container;
      const targetScrollTop = scrollTop + elementRect.top - containerRect.top - offset;

      container.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });

      return true;
    },
    [],
  );

  const updateHash = useCallback(
    (sectionId: string, resetDelay = 100) => {
      isUpdatingHashRef.current = true;
      history.replace(`${location.pathname}#${sectionId}`);
      setTimeout(() => {
        isUpdatingHashRef.current = false;
      }, resetDelay);
    },
    [history, location.pathname],
  );

  const scrollToSectionWithHash = useCallback(
    (sectionId: string, updateHashFlag = true) => {
      setIsManualScroll(false);
      if (updateHashFlag) {
        isUpdatingHashRef.current = true;
      }

      const scrolled = scrollToSection(sectionId);

      if (scrolled && updateHashFlag) {
        history.replace(`${location.pathname}#${sectionId}`);
      }

      setTimeout(() => {
        setIsManualScroll(true);
        if (updateHashFlag) {
          isUpdatingHashRef.current = false;
        }
      }, 1000);
    },
    [history, location.pathname, scrollToSection],
  );

  //* Functions
  const handleScroll = (sectionId: string) => {
    scrollToSectionWithHash(sectionId, true);
  };

  const changeNav = useCallback(() => {
    if (!scrollContainerRef.current || !isManualScroll) {
      return;
    }

    const scrollY = scrollContainerRef.current.scrollTop;

    // Find the current section based on scroll position
    for (let i = sections.length - 1; i >= 0; i -= 1) {
      const { id: sectionId, label } = sections[i];
      const section = document.getElementById(sectionId);
      if (section && scrollContainerRef.current) {
        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        const sectionRect = section.getBoundingClientRect();
        const sectionTop = scrollY + sectionRect.top - containerRect.top;
        if (scrollY >= sectionTop - 100) {
          if (activeSidebarLabel !== label) {
            setActiveSidebarLabel(label);
            updateHash(sectionId);
          }
          return;
        }
      }
    }

    // Default to first section if none found
    const defaultSectionId = 'phone-number-label';
    const defaultLabel = 'Phone Number Label';
    if (activeSidebarLabel !== defaultLabel) {
      setActiveSidebarLabel(defaultLabel);
      updateHash(defaultSectionId);
    }
  }, [isManualScroll, activeSidebarLabel, updateHash]);

  //* useEffect - Handle hash changes from URL (initial load and browser navigation)
  useEffect(() => {
    // Skip if we're updating the hash programmatically
    if (isUpdatingHashRef.current) {
      return;
    }

    const hash = location.hash.slice(1); // Remove the '#' character
    if (hash && sectionIdToLabel[hash]) {
      const label = sectionIdToLabel[hash];
      if (activeSidebarLabel !== label) {
        setActiveSidebarLabel(label);
        // Scroll to the section after a short delay to ensure DOM is ready
        setIsManualScroll(false);
        setTimeout(() => {
          scrollToSection(hash);
          setTimeout(() => {
            setIsManualScroll(true);
          }, 800);
        }, 100);
      }
    } else if (!hash && activeSidebarLabel !== 'Phone Number Label') {
      // If no hash, default to first section
      setActiveSidebarLabel('Phone Number Label');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, scrollToSection]);

  //* useEffect - Scroll listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', changeNav);
      return () => {
        scrollContainer.removeEventListener('scroll', changeNav);
      };
    }
    return undefined;
  }, [changeNav]);

  //* useEffect - Fetch phone number details on mount
  useEffect(() => {
    if (id) {
      const phoneNumberId = parseInt(id, 10);
      if (!Number.isNaN(phoneNumberId)) {
        dispatch(getPhoneNumberDetailsRequest(phoneNumberId));
      }
    }
  }, [id, dispatch]);

  //* useEffect - Handle error and redirect on request failure
  useEffect(() => {
    if (getPhoneNumberDetailsRequestStatus === RequestStatus.Failed) {
      history.push('/dialer/phone-numbers');
    }
  }, [getPhoneNumberDetailsRequestStatus, history]);


  return (
    <div className="phone-number-settings-container">
      <PhoneNumberSettingsSideBar
        scrollIntoView={handleScroll}
        activeSidebarLabel={activeSidebarLabel}
      />
      <div ref={scrollContainerRef} className="phone-number-settings-content">
        <PhoneNumberSettingsContent />
      </div>
    </div>
  );
};

export default PhoneNumberSettings;

